/**
 * Resize + compress an image File before community (or similar) uploads.
 * GIFs are returned as-is so animation is preserved.
 */

const DEFAULT_MAX_EDGE = 1920;
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;
const DEFAULT_TARGET_BYTES = 1.5 * 1024 * 1024;

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

/**
 * @param {File|Blob} file
 * @param {object} [opts]
 * @param {number} [opts.maxEdge] — longest side in px
 * @param {number} [opts.maxBytes] — hard reject/fail limit
 * @param {number} [opts.targetBytes] — preferred max after compress
 * @returns {Promise<File>}
 */
export async function compressImageFile(file, {
  maxEdge = DEFAULT_MAX_EDGE,
  maxBytes = DEFAULT_MAX_BYTES,
  targetBytes = DEFAULT_TARGET_BYTES,
} = {}) {
  if (!file) throw new Error('No image selected.');

  const type = (file.type || '').toLowerCase();
  if (type === 'image/gif') {
    if (file.size > maxBytes) throw new Error('Image must be 5 MB or smaller.');
    return file instanceof File ? file : new File([file], 'image.gif', { type: 'image/gif' });
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    if (file.size > maxBytes) throw new Error('Image must be 5 MB or smaller.');
    return file instanceof File ? file : new File([file], 'image.bin', { type: type || 'application/octet-stream' });
  }

  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height, 1));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      if (file.size > maxBytes) throw new Error('Image must be 5 MB or smaller.');
      return file instanceof File ? file : new File([file], 'image.bin', { type: type || 'application/octet-stream' });
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    let quality = 0.82;
    let blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    while (blob && blob.size > targetBytes && quality > 0.45) {
      quality = Math.round((quality - 0.08) * 100) / 100;
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    }
    while (blob && blob.size > maxBytes && quality > 0.28) {
      quality = Math.round((quality - 0.05) * 100) / 100;
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    }

    if (!blob) {
      if (file.size > maxBytes) throw new Error('Image must be 5 MB or smaller.');
      return file instanceof File ? file : new File([file], 'image.bin', { type: type || 'application/octet-stream' });
    }
    if (blob.size > maxBytes) throw new Error('Image must be 5 MB or smaller.');

    // Prefer original when JPEG is already smaller and we did not downscale.
    if (
      scale === 1
      && type === 'image/jpeg'
      && file.size <= maxBytes
      && file.size <= blob.size
    ) {
      return file instanceof File ? file : new File([file], 'photo.jpg', { type: 'image/jpeg' });
    }

    const base = String(file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
}

export default compressImageFile;
